import { useMemo, useState } from "react";
import { Plus, X, Eraser, Beaker, Repeat, Layers, Check, Search } from "lucide-react";

// =====================================================================
// ProceduresPalette — sidebar / sheet listing the clinic's procedures.
// Tap one to "arm" it; subsequent taps on the chart paint that
// procedure as a treatment-plan item on the tapped tooth/surface.
// =====================================================================

// Color hint per procedure category (so the dentist can scan visually).
// Falls back to slate if the category is unknown.
const CATEGORY_COLORS = {
  Restorative:        "#3b82f6", // blue
  Preventive:         "#0d9488", // teal
  Surgical:           "#ef4444", // red
  Endodontic:         "#8b5cf6", // violet
  Periodontic:        "#059669", // green
  Prosthodontic:      "#a16207", // gold
  Orthodontic:        "#ec4899", // pink
  Pediatric:          "#f59e0b", // amber
  Diagnostic:         "#6b7280", // gray
  "Oral Surgery":     "#dc2626", // dark red
  Cosmetic:           "#a855f7", // purple
};
function colorFor(category) {
  return CATEGORY_COLORS[category] || "#64748b";
}

export default function ProceduresPalette({
  procedures = [],
  active = null,           // { kind: 'procedure'|'erase'|'multi_tooth', id? } | null
  disabled = false,
  onPickProcedure,         // (procedure_id) => void
  onPickWholeMouth,        // (procedure) => void — non-tooth-specific procedure
  onAddMissingProcedure,   // (searchText) => void — create real clinic service, then add to plan
  onPickEraser,            // () => void
  onClear,                 // () => void
  onPickMultiTooth,        // () => void  — toggle Multi-Tooth Tool selection mode
  onDefineMultiTooth,      // () => void  — open Define dialog (called when buffer has teeth)
  multiToothCount = 0,     // # teeth currently in the multi-tooth selection buffer
  showMultiTooth = true,
}) {
  const armedId      = active?.kind === "procedure" ? active.id : null;
  const eraserOn     = active?.kind === "erase";
  const multiToothOn = active?.kind === "multi_tooth";

  function handlePickProcedure(p) {
    if (!p) return;
    if (p.tooth_specific === false && onPickWholeMouth) {
      onPickWholeMouth(p);
      return;
    }
    onPickProcedure?.(p.id);
  }

  // Search query — when non-empty, the accordion collapses into a
  // flat alphabetical list of matches across all categories.
  const [query, setQuery] = useState("");
  const trimmedQuery = query.trim().toLowerCase();

  // Filter + group. Search matches name + short_code + category.
  const { grouped, cats, searchResults } = useMemo(() => {
    const matches = (p) => {
      if (!trimmedQuery) return true;
      const haystack = [
        p.name || "",
        p.short_code || "",
        p.code || "",
        p.category || "",
      ].join(" ").toLowerCase();
      return haystack.includes(trimmedQuery);
    };
    if (trimmedQuery) {
      const flat = procedures
        .filter(matches)
        .slice()
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      return { grouped: {}, cats: [], searchResults: flat };
    }
    const g = {};
    for (const p of procedures) {
      const cat = p.category || "Other";
      (g[cat] = g[cat] || []).push(p);
    }
    return { grouped: g, cats: Object.keys(g).sort(), searchResults: null };
  }, [procedures, trimmedQuery]);

  return (
    <div className="bg-white rounded-2xl shadow-sm p-3 sm:p-4 space-y-3" data-testid="procedures-palette">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-bold tracking-wider uppercase text-slate-500">
          Procedures palette
        </p>
        {(armedId || eraserOn) ? (
          <button
            type="button"
            onClick={onClear}
            disabled={disabled}
            data-testid="procedures-clear"
            className="text-[11px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
          >
            <X className="w-3 h-3" /> Clear
          </button>
        ) : null}
      </div>

      {procedures.length === 0 ? (
        <div className="text-xs text-slate-400 px-1 py-3">
          No procedures in your catalog yet. Add some via Settings → Procedures.
        </div>
      ) : (
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search procedures…"
            disabled={disabled}
            data-testid="procedures-search"
            className="w-full pl-7 pr-7 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400 disabled:opacity-50"
          />
          {query ? (
            <button
              type="button"
              onClick={() => setQuery("")}
              data-testid="procedures-search-clear"
              aria-label="Clear search"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-700"
            >
              <X className="w-3 h-3" />
            </button>
          ) : null}
        </div>
      )}

      {searchResults ? (
        <div className="max-h-[420px] overflow-y-auto space-y-1 pr-1" data-testid="procedures-search-results">
          {searchResults.length === 0 ? (
            <div className="px-1 py-3 space-y-2">
              <p className="text-xs text-slate-400">No matches.</p>
              {trimmedQuery && onAddMissingProcedure ? (
                <button
                  type="button"
                  onClick={() => onAddMissingProcedure(query.trim())}
                  disabled={disabled}
                  data-testid="procedures-add-missing"
                  className="w-full inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-teal-200 bg-teal-50 text-xs font-semibold text-teal-700 hover:bg-teal-100 disabled:opacity-50"
                >
                  <Plus className="w-3.5 h-3.5" /> Add to my services
                </button>
              ) : null}
            </div>
          ) : (
            <ul className="space-y-1">
              {searchResults.map((p) => {
                const isArmed = armedId === p.id;
                const dot = colorFor(p.category);
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => handlePickProcedure(p)}
                      disabled={disabled}
                      data-testid={`procedure-${p.id}`}
                      title={
                        (p.visit_count > 1 ? "Multi-visit" : "Single visit")
                        + (p.requires_lab_work ? " · lab work" : "")
                        + ` — ${p.name}`
                      }
                      className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-sm font-medium transition-colors text-left focus:outline-none focus:ring-2 focus:ring-teal-400 focus:ring-offset-1 ${
                        isArmed
                          ? "border-slate-900 bg-slate-900 text-white"
                          : "border-slate-200 text-slate-700 bg-white hover:border-slate-400"
                      }`}
                    >
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0 ring-1 ring-black/10"
                        style={{ backgroundColor: dot }}
                      />
                      <span className="truncate flex-1">
                        {p.name}
                        <span className={`ml-1.5 text-[10px] ${isArmed ? "text-white/70" : "text-slate-400"}`}>
                          {p.category}
                        </span>
                      </span>
                      {p.visit_count > 1 ? (
                        <span className={`inline-flex items-center gap-0.5 text-[10px] tabular-nums px-1.5 py-0.5 rounded ${
                          isArmed ? "bg-white/20" : "bg-violet-50 text-violet-700"
                        }`}>
                          <Repeat className="w-2.5 h-2.5" />
                          Multi-visit
                        </span>
                      ) : null}
                      {p.requires_lab_work ? (
                        <Beaker
                          className={`w-3 h-3 shrink-0 ${isArmed ? "opacity-80" : "text-amber-600"}`}
                          aria-label="Lab work"
                        />
                      ) : null}
                      <span className="text-[10px] opacity-70 tabular-nums">
                        {formatProcedureFee(p.default_fee_centavos)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : null}

      <div
        className="max-h-[420px] overflow-y-auto space-y-3 pr-1"
        data-testid="procedures-list"
        style={searchResults ? { display: "none" } : undefined}
      >
        {cats.map((cat) => (
          <div key={cat} className="space-y-1">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 sticky top-0 bg-white pt-1">
              {cat}
            </p>
            <ul className="space-y-1">
              {grouped[cat].map((p) => {
                const isArmed = armedId === p.id;
                const dot = colorFor(p.category);
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => handlePickProcedure(p)}
                      disabled={disabled}
                      data-testid={`procedure-${p.id}`}
                      title={
                        (p.visit_count > 1 ? "Multi-visit" : "Single visit")
                        + (p.requires_lab_work ? " · lab work" : "")
                        + ` — ${p.name}`
                      }
                      className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-sm font-medium transition-colors text-left focus:outline-none focus:ring-2 focus:ring-teal-400 focus:ring-offset-1 ${
                        isArmed
                          ? "border-slate-900 bg-slate-900 text-white"
                          : "border-slate-200 text-slate-700 bg-white hover:border-slate-400"
                      }`}
                    >
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0 ring-1 ring-black/10"
                        style={{ backgroundColor: dot }}
                      />
                      <span className="truncate flex-1">{p.name}</span>
                      {p.visit_count > 1 ? (
                        <span
                          data-testid={`procedure-${p.id}-visits`}
                          className={`inline-flex items-center gap-0.5 text-[10px] tabular-nums px-1.5 py-0.5 rounded ${
                            isArmed ? "bg-white/20" : "bg-violet-50 text-violet-700"
                          }`}
                          title="Multi-visit — patient returns to finish"
                        >
                          <Repeat className="w-2.5 h-2.5" />
                          Multi-visit
                        </span>
                      ) : null}
                      {p.requires_lab_work ? (
                        <Beaker
                          className={`w-3 h-3 shrink-0 ${isArmed ? "opacity-80" : "text-amber-600"}`}
                          aria-label="Lab work"
                        />
                      ) : null}
                      <span className="text-[10px] opacity-70 tabular-nums">
                        {formatProcedureFee(p.default_fee_centavos)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      {/* Eraser + optional Multi-Tooth tools */}
      <div className={`pt-2 border-t border-slate-100 grid gap-2 ${showMultiTooth ? "grid-cols-2" : "grid-cols-1"}`}>
        <button
          type="button"
          onClick={onPickEraser}
          disabled={disabled}
          data-testid="procedures-eraser"
          className={`w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg border text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-rose-400 focus:ring-offset-1 ${
            eraserOn
              ? "border-rose-600 bg-rose-50 text-rose-700"
              : "border-slate-200 text-slate-700 bg-white hover:border-rose-400 hover:text-rose-600"
          }`}
        >
          <Eraser className="w-3.5 h-3.5" /> Eraser
        </button>

        {showMultiTooth ? (
          <>
        {/* Multi-Tooth Tool. Two-state behavior:
            - When the buffer is empty: clicking enters select mode.
            - When the buffer has ≥1 teeth: button transforms to
              "Define Multi-Tooth (N)" which opens the Define dialog. */}
        {multiToothOn && multiToothCount > 0 ? (
          <button
            type="button"
            onClick={onDefineMultiTooth}
            disabled={disabled}
            data-testid="procedures-multitooth-define"
            title="Define the multi-tooth procedure for the selected teeth"
            className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg border border-violet-600 bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-1"
          >
            <Check className="w-3.5 h-3.5" /> Define ({multiToothCount})
          </button>
        ) : (
          <button
            type="button"
            onClick={onPickMultiTooth}
            disabled={disabled}
            data-testid="procedures-multitooth"
            title="Multi-Tooth Tool — pick adjacent teeth for Bridge / Partial Denture"
            className={`w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg border text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-1 ${
              multiToothOn
                ? "border-violet-600 bg-violet-50 text-violet-700"
                : "border-slate-200 text-slate-700 bg-white hover:border-violet-400 hover:text-violet-700"
            }`}
          >
            <Layers className="w-3.5 h-3.5" /> Multi-Tooth
          </button>
        )}
          </>
        ) : null}
      </div>
    </div>
  );
}

function formatProcedureFee(centavos) {
  if (centavos == null) return "Depends on case";
  return `₱${Math.round(centavos / 100).toLocaleString()}`;
}
